/**
 * Field Agent Weekly Rollup — Phase 2B (2026-04-23)
 * /portal/field-agent/weekly-rollup
 *
 * Read-only summary of the current and previous 3 weeks of activity,
 * derived live from /api/field-activities/weekly-rollup.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Loader2, ArrowLeft, Plus, Image as ImageIcon, AlertTriangle, CalendarCheck2 } from "lucide-react";
import AgentRenewalBanner from "../../components/fieldAgent/AgentRenewalBanner";
import PayoutReadinessPill from "../../components/fieldAgent/PayoutReadinessPill";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-JM", { month: "short", day: "numeric" });
}

export default function FieldAgentWeeklyRollup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rollups, setRollups] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/field-activities/weekly-rollup?weeks=4");
        setRollups(res.data.rollups || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 403) setError("Your account is not set up as a field agent yet. If you think this is wrong, contact Mark.");
        else if (status === 401) setError("Please sign in first to see your rollup.");
        else setError(e?.response?.data?.detail?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : null) || "Could not load rollup. Please try again in a minute.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="max-w-3xl mx-auto px-4 pt-3"><AgentRenewalBanner /></div><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="field-agent-weekly-rollup-page">
      <div className="max-w-xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Weekly rollup</h1>
            <p className="text-xs text-slate-500">Last 4 weeks · America/Jamaica</p>
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate("/portal/field-agent/log-entry")} data-testid="weekly-new-log"><Plus className="w-3 h-3 mr-1" />New</Button>
        </div>

        {error && <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-3" data-testid="rollup-error">{String(error)}</div>}

        {!error && rollups.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="rollup-empty">
            <p className="text-sm text-slate-500">No activity yet. Start logging visits to see your weekly summary.</p>
          </div>
        )}

        <div className="space-y-3">
          {rollups.map((r, idx) => (
            <section key={r.week_of} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`rollup-week-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-base font-semibold">{formatDate(r.week_of)} – {formatDate(r.week_end)}</h2>
                  <p className="text-xs text-slate-500">{idx === 0 ? "This week" : idx === 1 ? "Last week" : `${idx} weeks ago`}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-800" data-testid={`rollup-total-${idx}`}>{r.total_activities}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">activities</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                <div className="bg-emerald-50 rounded-lg p-2 text-center">
                  <div className="font-semibold text-emerald-700">{r.by_status?.submitted || 0}</div>
                  <div className="text-emerald-600/70">submitted</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                  <div className="font-semibold text-amber-700">{r.by_status?.draft || 0}</div>
                  <div className="text-amber-600/70">drafts</div>
                </div>
                <div className="bg-slate-100 rounded-lg p-2 text-center">
                  <div className="font-semibold text-slate-700">{r.days_active}</div>
                  <div className="text-slate-500">days active</div>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-slate-600 mb-2">
                <span className="inline-flex items-center gap-1" data-testid={`rollup-evidence-${idx}`}><ImageIcon className="w-3 h-3" /> {r.evidence_total} photos · {r.activities_with_evidence} w/ evidence · {r.activities_missing_evidence} missing</span>
              </div>
              {r.activities_with_duplicate_flag > 0 && (
                <div className="flex items-center gap-1 text-xs text-amber-700 mb-2" data-testid={`rollup-dup-${idx}`}>
                  <AlertTriangle className="w-3 h-3" /> {r.activities_with_duplicate_flag} duplicate-flagged
                </div>
              )}
              {r.follow_ups_due_this_week > 0 && (
                <div className="flex items-center gap-1 text-xs text-indigo-700 mb-2" data-testid={`rollup-followups-${idx}`}>
                  <CalendarCheck2 className="w-3 h-3" /> {r.follow_ups_due_this_week} follow-ups due
                </div>
              )}

              {r.total_activities > 0 && (
                <>
                  <div className="text-[11px] font-medium text-slate-500 mt-3 mb-1">By prospect type</div>
                  <div className="flex flex-wrap gap-1" data-testid={`rollup-ptype-${idx}`}>
                    {Object.entries(r.by_prospect_type || {}).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                    ))}
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-3 mb-1">By outcome</div>
                  <div className="flex flex-wrap gap-1" data-testid={`rollup-outcome-${idx}`}>
                    {Object.entries(r.by_visit_outcome || {}).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px]">{k.replace(/_/g, " ")}: {v}</Badge>
                    ))}
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-3 mb-1">By parish</div>
                  <div className="flex flex-wrap gap-1" data-testid={`rollup-parish-${idx}`}>
                    {Object.entries(r.by_parish || {}).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                    ))}
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-3 mb-1">By conversion intent</div>
                  <div className="flex flex-wrap gap-1" data-testid={`rollup-intent-${idx}`}>
                    {Object.entries(r.by_conversion_intent || {}).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                    ))}
                  </div>
                </>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
